// TESSERAX — COACH DE FIN DE PARTIE (cœur pur, testé).
//
// L'écran de fin donnait déjà UN conseil ciblé, mais via une cascade if/else figée à
// QUATRE cas (Bascule / trous / Zone / hauteur) vivant inline dans main.ts (`@ts-nocheck`,
// non testée). Conséquence silencieuse : le jeu ne coachait JAMAIS les vraies habitudes
// qui plafonnent le score — n'effacer que des lignes SIMPLES (jamais un étage / une Croix),
// ne jamais ENCHAÎNER (combo faible), ignorer la Croix (le feat propre au puits 3D). Et la
// cascade n'était pas PRIORISÉE : un run criblé de trous pouvait recevoir le conseil « Zone »
// au lieu du conseil le plus urgent. On isole donc ici un MOTEUR DE COACHING : il analyse le
// run sur plusieurs dimensions, attribue à chacune une priorité (le LEVIER de progression),
// et surface le conseil le plus impactant — avec un repli ASPIRATIONNEL (renforcement positif)
// quand le joueur joue déjà bien, pour que le coach encourage au lieu de seulement gronder.
//
// Module PUR : ni THREE ni DOM, pas de Math.random, pas d'I/O. main.ts mappe la clé i18n en
// libellé (`t(key, …, fallbackFr)`). C'est de l'AFFICHAGE seul → aucun RNG touché, déterminisme
// du défi du jour intact. Data-driven : on enrichit le coaching en AJOUTANT une règle.

export interface CoachStats {
  bascules: number;   // nombre de Bascules jouées
  usedZone: boolean;  // le pouvoir Zone a-t-il été déclenché au moins une fois ?
  holes: number;      // cases vides scellées sous la pile (holeCount)
  lines: number;      // total de lignes effacées
  slabs: number;      // étages pleins (49 cases) effacés
  cross: number;      // effacements en CROIX (deux axes horizontaux d'un coup)
  series: number;     // meilleure SÉRIE / combo de la partie
  continuum: number;  // plus long CONTINUUM (back-to-back de maîtrise)
  perfects: number;   // nombre de PERFECT (puits entièrement vidé)
  level: number;      // profondeur atteinte
  reason: string;     // cause de fin : 'time' | 'goal' | 'height' | 'holes' | 'zen' | …
  zen: boolean;       // partie en mode Zen (pas de défaite « par hauteur »)
  tesseract?: boolean; // partie en mode 4D « Le Pli » (active les conseils Pli/Tessera)
  folds?: number;     // nombre de Plis joués (mode tesseract)
  tesseras?: number;  // effacements TESSERAX (effacement 4D exigeant les deux chambres)
}

export interface CoachTip {
  key: string;        // clé i18n (FR/EN dans les dictionnaires)
  fallbackFr: string; // libellé FR de repli → module autonome / affichable sans i18n
  priority: number;   // levier de progression (plus haut = plus impactant pour CE run)
  tone: 'fix' | 'praise'; // 'fix' = corrige une habitude ; 'praise' = renforcement positif
}

// Entier propre et borné : tolère NaN / Infinity / négatifs (entrées corrompues) → 0.
function clean(n: number): number {
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

// Liste PRIORISÉE des conseils applicables à ce run, du plus impactant au moins. Chaque règle
// est indépendante et data-driven ; la priorité encode le LEVIER (mécanique signature ignorée
// > habitude qui plafonne le score > survie > aspiration). Déterministe : tri stable, départage
// par ordre de déclaration. Garantit toujours AU MOINS un conseil (repli hauteur, priorité 5).
export function coachTips(stats: CoachStats): CoachTip[] {
  const bascules = clean(stats.bascules);
  const holes = clean(stats.holes);
  const lines = clean(stats.lines);
  const slabs = clean(stats.slabs);
  const cross = clean(stats.cross);
  const series = clean(stats.series);
  const continuum = clean(stats.continuum);
  const perfects = clean(stats.perfects);
  const usedZone = !!stats.usedZone;
  const zen = !!stats.zen;
  const tess = !!stats.tesseract, folds = clean(stats.folds), tesseras = clean(stats.tesseras);

  const out: CoachTip[] = [];
  const push = (cond: boolean, key: string, fallbackFr: string, priority: number, tone: CoachTip['tone'] = 'fix') => {
    if (cond) out.push({ key, fallbackFr, priority, tone });
  };

  // 0. LE PLI (mode 4D) — mécanique signature du mode : jamais REPLIÉ → le plus gros levier ici.
  //    On empile dans une chambre, on plie (B) pour combler dans l'AUTRE et aligner une TESSERAX.
  push(tess && folds === 0, 'end.tipFold',
    'plie (B) pour passer dans l’autre chambre et y combler tes trous → une rangée bouclée par les DEUX chambres = TESSERAX', 110);
  // 0b. A replié mais n'a jamais aligné de Tessera → on enseigne le feat 4D le mieux payé.
  push(tess && folds > 0 && tesseras === 0, 'end.tipTessera',
    'laisse des trous dans une chambre, plie, comble-les dans l’autre : la rangée s’efface dès que les DEUX chambres la couvrent → TESSERAX, le feat 4D le mieux payé', 108);

  // 1. Mécanique SIGNATURE ignorée — la Bascule comble les trous ET lance les cascades : c'est
  //    le plus gros levier pour qui ne l'a jamais armée. Toujours en tête.
  push(bascules === 0, 'end.tipBascule',
    'arme la Bascule (T) pour combler les trous et lancer des cascades', 100);

  // 2. Trous SCELLÉS sous la pile — tueur de survie ; priorité ESCALADANTE avec la sévérité
  //    (un run très troué doit primer sur un conseil générique), plafonnée sous la Bascule.
  push(holes >= 5, 'end.tipHoles',
    'évite de couvrir des cases vides — elles condamnent les lignes du dessous',
    70 + Math.min(holes, 18));

  // 3. Pouvoir ZONE jamais déclenché alors que le joueur s'est assez engagé (≥5 lignes) pour en
  //    profiter — conseil prématuré sur un run avorté, donc gardé.
  push(!usedZone && lines >= 5, 'end.tipZone',
    'déclenche la Zone (F) dès que la jauge est pleine', 65);

  // 4. DENSITÉ — n'effacer QUE des lignes simples : beaucoup de lignes mais jamais un étage, ni
  //    une Croix, ni un enchaînement. Habitude qui plafonne le score (un double/étage paie bien
  //    plus que deux simples). NOUVELLE dimension de coaching, jamais surfacée auparavant.
  push(lines >= 8 && slabs === 0 && cross === 0 && series < 2 && continuum < 2,
    'end.tipDensity', 'empile plus large avant d’effacer — un étage plein paie bien plus que des lignes simples',
    55);

  // 5. CROIX — le feat propre au puits 3D (effacer les DEUX axes horizontaux d'un coup), le clear
  //    le mieux payé. Jamais réalisé malgré un run engagé → on l'enseigne. NOUVELLE dimension.
  push(cross === 0 && lines >= 10, 'end.tipCross',
    'vise la CROIX : efface les deux axes horizontaux d’un même coup — le feat 3D qui paie le plus',
    45);

  // 6. COMBO — enchaîner des effacements sur des pièces consécutives multiplie le score ; une
  //    série toujours < 2 signale un jeu « coup par coup ». NOUVELLE dimension.
  push(series < 2 && lines >= 10, 'end.tipCombo',
    'enchaîne tes effacements pour monter le combo — chaque maillon multiplie la mise', 40);

  // 7. Repli HAUTEUR (cause de défaite la plus commune) — conseil d'aplatissement.
  push(!zen, 'end.tipHeight', 'aplatis les couches hautes avant qu’elles ne montent', 30);

  // 8. ASPIRATION / renforcement positif — le joueur fait déjà l'essentiel (Bascule, Zone, un
  //    feat dense) sans faille criante : on l'encourage vers le sommet plutôt que de gronder.
  //    Placé JUSTE au-dessus du repli hauteur (30) : une vraie correction (Croix, combo…) prime
  //    encore, mais sur un run propre le coach félicite au lieu de servir le conseil générique.
  const masterful = bascules > 0 && usedZone && (slabs > 0 || cross > 0 || series >= 3) && holes < 5;
  push(masterful && perfects === 0, 'end.tipMastery',
    'beau jeu — vise maintenant un PERFECT : vide entièrement le puits pour le bonus suprême', 36, 'praise');
  push(masterful && perfects > 0, 'end.tipDeepen',
    'maîtrise solide — creuse un CONTINUUM plus long (clears de maîtrise enchaînés) pour exploser le score', 34, 'praise');

  // Garde-fou : toujours au moins un conseil, même sur un run dégénéré (Zen sans rien d'autre).
  push(out.length === 0, 'end.tipHeight', 'aplatis les couches hautes avant qu’elles ne montent', 5);

  // Tri stable décroissant par priorité (départage par ordre d'insertion → déterminisme).
  return out
    .map((tip, i) => ({ tip, i }))
    .sort((a, b) => (b.tip.priority - a.tip.priority) || (a.i - b.i))
    .map(x => x.tip);
}

// Le conseil le plus impactant pour ce run (toujours défini). C'est ce que main.ts affiche.
export function coachRun(stats: CoachStats): CoachTip {
  return coachTips(stats)[0];
}
